import { hillScore, type HillScore } from "./hill-score";
import type { HillLessonHud } from "./hill-tutorial";
import type { Skater } from "./skater";
import type { HillEnd, HillHit } from "./sim/state";
import type { RideId } from "./sim/rides";
import { WOBBLE_SPEED } from "./sim/tuning";

// Bomb the Hill (#193) outside the sim: the best distance and best score
// (#205, kept in localStorage, per hill) and what the HUD shows of a run. Sim-free, so
// `Game` only feeds it; `ui/HillHud.tsx` draws it.

/** A screen this short (CSS px) or less is a phone held sideways, whatever its pointer (#198). */
export const PHONE_HEIGHT = 500;

/**
 * A phone-sized screen (#198): a touch device, or one this short. On the
 * hill it gets the small HUD, only the hill's touch keys, the icon row
 * folded into Pause and a camera further back.
 */
export function phoneScreen(touch: boolean, height: number): boolean {
  return touch || height <= PHONE_HEIGHT;
}

/** What the HUD shows on a hill: the run's distance, the speed against the wobbles, and how the run ended. */
export interface HillHud {
  /** Metres down the hill this run, whole, close calls (#196) included. */
  distance: number;
  /** Close calls with people on crosswalks this run (#196). */
  closeCalls: number;
  /** The best run down this hill, whole metres. */
  best: number;
  /** The run's points so far (#205, `hill-score.ts`): speed and tricks on their own, and the weighted total with the distance. */
  speedPts: number;
  trickPts: number;
  score: number;
  /** The best total down this hill. */
  bestScore: number;
  /** Speed (km/h). */
  speed: number;
  /** Speed against where the wobbles start: 1 is wobble speed (0 .. 1.4, in steps of 0.02). */
  gauge: number;
  /** The speed wobbles, 0 .. 1 in steps of 0.05. */
  wobble: number;
  /** The wheels sliding out of a hard carve. */
  sliding: boolean;
  /** How the run ended (and what it ran into, #194), its distance, and whether it beat the best; null while it's on. */
  end: HillRunEnd | null;
  /** On a gravel patch (#194): no grip to carve. */
  gravel: boolean;
  /** At the top picking a ride (#195): the one picked so far; null once you've dropped in. */
  picking: RideId | null;
  /** This run's road seed (#195): the same seed is the same road. */
  seed: number;
  /** The tutorial's lesson (#201), while it runs. */
  lesson?: HillLessonHud | null;
}

/** How a run ended, for the end card: its distance and whether that's a best, and (#205) its scores and whether the total's a best. */
export interface HillRunEnd {
  why: HillEnd;
  hit: HillHit | null;
  distance: number;
  best: boolean;
  score?: HillScore & { best: boolean };
}

/** What each way a run ends is called out as. */
export const HILL_END_TEXT: Record<HillEnd, string> = {
  plant: "FACE PLANT!",
  slide: "WHEELS OUT!",
  crash: "WIPED OUT!",
  hit: "SMACK!",
  bottom: "MADE IT DOWN!",
};

/** What running into each thing in the road (#194) is called out as. */
export const HILL_HIT_TEXT: Record<HillHit, string> = {
  wall: "INTO THE WALL!",
  car: "INTO A CAR!",
  van: "INTO A VAN!",
  bus: "INTO THE BUS!",
  cone: "CONED!",
  bin: "BINNED IT!",
  barrier: "HIT THE BARRIER!",
  rail: "CLIPPED A RAIL!",
  pothole: "POTHOLE!",
  gravel: "GRAVEL!",
  pedestrian: "SORRY!",
};

/** The end card's call-out: what you hit, or how the run ended. */
export function hillEndText(end: { why: HillEnd; hit: HillHit | null }): string {
  return end.hit ? HILL_HIT_TEXT[end.hit] : HILL_END_TEXT[end.why];
}

const KEY = "kickflip.hillBest";
/** The best total (#205) down each hill, kept beside the best distance. */
export const SCORE_KEY = "kickflip.hillBestScore";

/** The best distance down each hill, by map id (or, with `SCORE_KEY`, the best total, #205). */
export function loadHillBests(store: Storage | null, key = KEY): Record<string, number> {
  try {
    const raw: unknown = JSON.parse(store?.getItem(key) ?? "{}");
    if (!raw || typeof raw !== "object" || Array.isArray(raw)) return {};
    return Object.fromEntries(Object.entries(raw).filter((e): e is [string, number] => typeof e[1] === "number" && Number.isFinite(e[1]) && e[1] >= 0));
  } catch {
    return {};
  }
}

export function saveHillBests(store: Storage | null, bests: Record<string, number>, key = KEY): void {
  try {
    store?.setItem(key, JSON.stringify(bests));
  } catch {
    // Storage full or refused (the artifact frame): the best lasts the session.
  }
}

/** Builds the hill's HUD, handing back the last one while nothing on it has changed (the HUD diff checks identity). */
export class HillHudTracker {
  private last: HillHud | null = null;
  /** How the last run ended, kept until the next starts: its distance and whether it was a best. */
  private ended: HillHud["end"] = null;

  /**
   * A run ended at `distance` (m) with `best` the best before it (on `hit`,
   * #194), and (#205) its `score` against `bestScore`, the best total before
   * it. Returns whether each is a new best.
   */
  end(why: HillEnd, distance: number, best: number, hit: HillHit | null = null, score?: HillScore, bestScore = 0): { distance: boolean; score: boolean } {
    const isBest = distance > best;
    const scoreBest = score !== undefined && score.total > bestScore;
    this.ended = score ? { why, hit, distance, best: isBest, score: { ...score, best: scoreBest } } : { why, hit, distance, best: isBest };
    return { distance: isBest, score: scoreBest };
  }

  hud(sk: Skater, best: number, lesson: HillLessonHud | null = null, bestScore = 0): HillHud | null {
    const h = sk.hill;
    if (!h) return (this.last = null);
    if (!h.end) this.ended = null;
    const speed = sk.horizontalSpeed;
    const score = hillScore(h);
    const next: HillHud = {
      distance: Math.floor(h.distance + h.bonus),
      closeCalls: h.closeCalls,
      best: Math.floor(best),
      speedPts: score.speed,
      trickPts: score.tricks,
      score: score.total,
      bestScore: Math.floor(bestScore),
      speed: Math.round(speed * 3.6),
      gauge: Math.min(1.4, Math.round((speed / WOBBLE_SPEED) * 50) / 50),
      wobble: Math.round(h.wobble * 20) / 20,
      sliding: h.sliding,
      gravel: h.gravel,
      end: this.ended,
      picking: h.picking ? sk.ride : null,
      seed: h.seed,
      lesson,
    };
    const prev = this.last;
    if (prev && (Object.keys(next) as (keyof HillHud)[]).every((k) => prev[k] === next[k])) return prev;
    return (this.last = next);
  }
}
